import React, { useState, useEffect, useCallback } from 'react'

import { isValidCssValue } from '../../lib/cssValue'

/**
 * A text field for theme values that are CSS, not prose.
 *
 * The generic `TextInput` pushes every keystroke straight to the store with no
 * screening, which is right for a transcript notice and wrong for a value
 * headed into `setProperty()`. An unparseable CSS shorthand is DROPPED by the
 * browser while still reading as non-empty here -- so the control paints
 * nothing, and `hasTheme()` still counts the preference as themed, which opts
 * it out of fill-only fan-out. Nothing anywhere says so.
 *
 * Same contract as the colour field: push only what parses, keep the draft on
 * screen so a near-miss can be corrected rather than retyped, and say why when
 * something is refused.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {string} props.cssProperty      the real property to screen against
 * @param {string} [props.placeholder]
 * @param {string} props.preferenceName
 * @param {(value: string) => void} props.storeEdits
 */
const CssTextInput = props => {
  const { value, cssProperty, placeholder, preferenceName, storeEdits } = props

  const [draft, setDraft] = useState(value || '')
  const [rejected, setRejected] = useState(false)

  useEffect(() => {
    setDraft(value || '')
  }, [value])

  const handleChange = useCallback(ev => {
    setDraft(ev.target.value)
    setRejected(false)
  }, [])

  const handleBlur = useCallback(() => {
    const next = draft.trim()
    if (!isValidCssValue(cssProperty, next)) {
      setRejected(true)
      return
    }
    setRejected(false)
    if (next !== value) storeEdits(next)
  }, [draft, value, cssProperty, storeEdits])

  return (
    <div className={'theme-css-text' + (rejected ? ' theme-css-text--rejected' : '')}>
      <input
        type="text"
        className="form-control"
        name={preferenceName}
        value={draft}
        placeholder={placeholder || ''}
        aria-invalid={rejected}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {rejected ? (
        <p className="theme-css-text__note" role="alert">
          Not a value the player can use. It would be discarded and the control
          would appear to do nothing.
        </p>
      ) : null}
    </div>
  )
}

export default CssTextInput
